import { useState } from 'react'
import { Drawer, Tabs, Button, Spin } from 'antd'
import styled from 'styled-components'
import { BasTitle } from '@/components'
import { TagDetail, CodeSnippet } from '@/components'
import MarkdownEditor from '@/components/markdown-editor'
import DetailLog from './detail-log'
import { t } from '@/i18n'

export const DetailDrawer = props => {
  const { visible = true, onClose, dataList, logList = [], loading } = props
  const [encode, setEncode] = useState(true)
  const contentSize = 1024 * 50

  const resourceLevelMap = {
    ResourceLevelJunior: { label: t('初级'), class: 'case-level-low' },
    ResourceLevelMiddle: { label: t('中级'), class: 'case-level-center' },
    ResourceLevelSenior: { label: t('高级'), class: 'case-level-high' },
  }

  // 验证模式组件
  const VerificationMode = () => {
    const modeMap = {
      '2': t('Web攻击机 → Web应用'),
      '1': t('Web应用 → Web攻击机'),
    }
    return <span>{modeMap[dataList?.verification_mode_id] || '-'}</span>
  }

  // 验证结果组件
  const VerificationResult = () => (
    <span style={{ display: 'flex' }}>
      <StatusTag status={dataList?.intercept_status}>{dataList?.intercept_status}</StatusTag>
      {dataList?.intercept_from === 'interception_from_manual_correct' && (
        <span
          style={{
            marginLeft: 5,
            borderRadius: 6,
            padding: '0 2px',
            fontSize: 12,
            color: '#1bbc40',
            background: '#d9f4df',
            height: 20,
            lineHeight: '20px',
          }}
        >
          {t('已修正')}
        </span>
      )}
    </span>
  )

  // WAF厂商组件
  const WafFirm = () => {
    if (!dataList?.waf_firm?.length) {
      return <p className="name">{t('其他')}</p>
    }
    return (
      <p className="name">
        {dataList.waf_firm.map(item => (
          <span key={item} className="waf">
            {item}
          </span>
        ))}
      </p>
    )
  }

  // 漏洞编号组件
  const VulnerabilityIds = () => {
    const hasVulnerabilities =
      dataList?.CVE_ID?.length > 0 ||
      dataList?.CNVD_ID?.length > 0 ||
      dataList?.CNNVD_ID?.length > 0

    if (!hasVulnerabilities) return null

    return (
      <div className="flex">
        <p className="title">{t('漏洞编号：')}</p>
        <div className="name">
          {dataList.CVE_ID?.map((item, index) => (
            <p key={index}>
              <a href={item.href} target="_blank" rel="noopener noreferrer">
                {item.name}
              </a>
            </p>
          ))}
          {dataList.CNVD_ID?.map((item, index) => (
            <p key={index}>
              <span>{item.name}</span>
            </p>
          ))}
          {dataList.CNNVD_ID?.map((item, index) => (
            <p key={index}>
              <span>{item.name}</span>
            </p>
          ))}
        </div>
      </div>
    )
  }

  // ATT&CK 组件
  const AttackTactics = () => {
    if (!dataList?.attck?.length) return null

    return (
      <div className="flex">
        <p className="title">ATT&CK：</p>
        <div className="name">
          {dataList.attck.map((item, index) => (
            <p key={index}>
              <a href={item?.attck_url} target="_blank" rel="noopener noreferrer">
                {item?.attck_name}
              </a>
            </p>
          ))}
        </div>
      </div>
    )
  }

  const BasicInfo = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <>
          <div className="flex">
            <p className="title">{t('用例名称：')}</p>
            <div style={{ lineHeight: '1.8em', marginTop: '-5px' }}>{dataList?.payload_name}</div>
          </div>
          <div className="flex">
            <p className="title">{t('验证模式：')}</p>
            <VerificationMode />
          </div>
          <div className="flex">
            <p className="title">{t('用例分级：')}</p>
            <span>{resourceLevelMap[dataList?.resource_level]?.label || '-'}</span>
          </div>
          <div className="flex">
            <p className="title">{t('验证结果：')}</p>
            <VerificationResult />
          </div>
          <div className="flex">
            <p className="title">{t('危险等级：')}</p>
            <span>{dataList?.risk_level}</span>
          </div>
          <div className="flex">
            <p className="title">{t('攻击类型：')}</p>
            <p className="name">{dataList?.attack_type || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">OWASP TOP10：</p>
            <p className="name">{dataList?.owasp?.join('、') || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">WASC：</p>
            <p className="name">{dataList?.wasc?.join('、') || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">{t('操作系统：')}</p>
            <p className="name">{dataList?.os || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">{t('数据库：')}</p>
            <p className="name">{dataList?.db || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">{t('开发语言：')}</p>
            <p className="name">{dataList?.language || '-'}</p>
          </div>
          <div className="flex">
            <p className="title">{t('影响组件：')}</p>
            <p className="name">{dataList?.component || '无'}</p>
          </div>
          <div className="flex">
            <p className="title">{t('WAF厂商：')}</p>
            <WafFirm />
          </div>
          <VulnerabilityIds />
          <AttackTactics />
          {dataList?.resource_tag_list && dataList?.resource_tag_list?.length > 0 && (
            <TagDetail resourceTagList={dataList?.resource_tag_list || []} />
          )}
          {dataList?.describe && (
            <div className="block">
              <p className="title">{t('描述：')}</p>
              <MarkdownEditor
                isEdit={false}
                onlyDisplay
                setValue={() => {}}
                value={dataList?.describe}
              />
            </div>
          )}
          {dataList?.solution && (
            <div className="block">
              <p className="title">{t('解决方案：')}</p>
              <MarkdownEditor
                isEdit={false}
                onlyDisplay
                setValue={() => {}}
                value={dataList?.solution}
              />
            </div>
          )}
        </>
      )}
    </DetailDrawerBox>
  )

  const AttackDetail = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <CodeBox>
          <div className="block" style={{ marginTop: 0 }}>
            <p className="title">
              Payload：
              {dataList?.enctype_type && (
                <Button
                  type="primary"
                  onClick={() => {
                    setEncode(!encode)
                  }}
                >
                  {encode ? t('解码') : t('编码')}
                  {`(${dataList?.enctype_type?.slice(0, dataList.enctype_type.length - 2)})`}
                </Button>
              )}
            </p>
            <div className="content header">
              <div style={{ display: 'flex' }}>
                {encode ? (
                  dataList?.payload?.length > contentSize ? (
                    <Button type="link" onClick={() => downloadTxt(dataList?.payload, 'payload_')}>
                      {t('下载')}
                    </Button>
                  ) : (
                    <CodeSnippet
                      code={[encode ? dataList?.payload : dataList?.payload_decode]}
                      type={4}
                      language="html"
                    />
                  )
                ) : dataList?.payload_decode.length > contentSize ? (
                  <Button
                    type="link"
                    onClick={() => downloadTxt(dataList?.payload_decode, 'payload_')}
                  >
                    {t('下载')}
                  </Button>
                ) : (
                  <CodeSnippet
                    code={[encode ? dataList?.payload : dataList?.payload_decode]}
                    type={4}
                    language="html"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="block">
            <p className="title">
              <span>URL：</span>
            </p>
            <div className="content header">
              <CodeSnippet code={[dataList?.url]} type={4} language="http" />
            </div>
          </div>
          <div className="flex">
            <p className="title" style={{ width: 20 }}>
              {t('源IP：')}
            </p>
            <p className="name">{dataList?.source_ip || '-'}</p>
          </div>
          <div className="block">
            <p className="title">
              <span>{t('HTTP请求：')}</span>
            </p>
            <div className="content header">
              {dataList?.http_request?.body.length > contentSize ? (
                <Button
                  type="link"
                  onClick={() => downloadTxt(dataList?.request_copy, 'http _request_')}
                >
                  {t('下载')}
                </Button>
              ) : (
                <CodeSnippet
                  code={[
                    dataList?.http_request.headers,
                    dataList?.http_request?.body,
                    `${dataList?.http_request?.method} ${dataList?.http_request?.path} ${dataList?.http_request?.protocol}`,
                  ]}
                  copyText={dataList?.request_copy?.join('\r\n')}
                  type={6}
                  language="html"
                />
              )}
            </div>
          </div>
          <div className="block">
            <p className="title">
              <span>{t('HTTP响应：')}</span>
            </p>
            <div className="content header">
              {dataList?.http_response?.body.length > contentSize ? (
                <Button
                  type="link"
                  onClick={() => downloadTxt(dataList?.http_response?.body, 'http _response_')}
                >
                  {t('下载')}
                </Button>
              ) : (
                <CodeSnippet
                  code={[
                    dataList?.http_response?.headers,
                    dataList?.http_response?.body,
                    `${dataList?.http_response?.protocol} ${dataList?.http_response?.msg}`,
                  ]}
                  copyText={dataList?.response_copy?.join('\r\n')}
                  type={6}
                  language="html"
                />
              )}
            </div>
          </div>
        </CodeBox>
      )}
    </DetailDrawerBox>
  )

  const LogDetail = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <>
          <div className="flex">
            <p className="title">{t('告警日志：')}</p>
          </div>
          {logList.map((item, index) => (
            <DetailLog key={index} data={item} index={index} />
          ))}
        </>
      )}
    </DetailDrawerBox>
  )

  const downloadTxt = (content, name) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    if ((window.navigator as any)?.msSaveOrOpenBlob) {
      ;(navigator as any)?.msSaveBlob(blob, `${name}detail.txt`)
    } else {
      const a = document.createElement('a')
      const url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = `${name}detail.txt`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    }
  }

  return (
    <DetailDrawerContainer>
      <DrawerBox
        title={<BasTitle title={t('用例详情')} />}
        placement="right"
        closable
        onClose={onClose}
        visible={visible}
        width={600}
        destroyOnClose
      >
        <Tabs
          defaultActiveKey="1"
          items={
            logList.length > 0
              ? [
                  { label: t('用例信息'), key: '1', children: <BasicInfo /> },
                  { label: t('攻击详情'), key: '2', children: <AttackDetail /> },
                  { label: t('告警日志'), key: '3', children: <LogDetail /> },
                ]
              : [
                  { label: t('用例信息'), key: '1', children: <BasicInfo /> },
                  { label: t('攻击详情'), key: '2', children: <AttackDetail /> },
                ]
          }
        />
      </DrawerBox>
    </DetailDrawerContainer>
  )
}

const DetailDrawerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const DrawerBox = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }
`

const DetailDrawerBox = styled.div`
  .block {
    margin: 12px 0;
    .title {
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
    }
  }
  .content {
    word-break: break-all;
    color: #666666;
    border-radius: 4px;
    .headers {
      display: flex;
      margin-bottom: 5px;
    }
    .marked {
      white-space: nowrap;
      color: #558490;
      margin-right: 5px;
    }
    .marked-body {
      color: #558490;
    }
  }
  .header {
    // max-height: 200px;
    overflow-y: auto;
  }
  .flex,
  .flex-name {
    display: flex;
    margin-bottom: 10px;
    .content {
      color: #666666;
      border-radius: 4px;
      padding: 10px;
      width: calc(98% - 128px);
      background: #f7f8fa;
    }
    .title {
      height: 14px;
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
    }
    .name {
      flex: 1;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .flex-name {
    width: 50%;
  }
  .flex-two {
    display: flex;
    .title {
      height: 14px;
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
    }

    .name {
      width: 55%;
    }
  }

  .intercept,
  .intercept-err {
    width: 64px;
    height: 20px;
    background: #e5eeff;
    border-radius: 4px 12px 4px 12px;
    font-size: 12px;
    color: #2973ff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .intercept-err {
    color: #f44f4f;
    background: #fde5e5;
  }
`

const CodeBox = styled.div`
  .title {
    margin-bottom: 0;
  }
`

interface StatusTagProps {
  status: string
}
const StatusTag = styled.div<StatusTagProps>`
  font-size: 12px;
  background: ${props => (props.status === t('拦截成功') ? '#E5EEFF' : '#FDE5E5')};
  color: ${props => (props.status === t('拦截成功') ? '#2973FF' : '#F44F4F')};
  border-radius: 4px 12px 4px 12px;
  width: 64px;
  height: 20px;
  line-height: 20px;
  text-align: center;
`
